import { storeToRefs } from 'pinia'
import {
  computed,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import {
  MAP_CENTER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
  MAP_ZOOM,
} from '../constants'
import { transform } from '../services/map/ProjectionService'
import { useDetailStore } from '../stores/useDetailStore'
import { useDetailMapHelpers } from './useDetailMapHelpers'

export function useMicroMacroDetailMap(options) {
  const { findLayer } = useDetailMapHelpers()
  const { t } = useI18n()
  const {
    mapType, // 'micro' or 'macro'
    featureLayerId,
    infoLayersIds,
    createMapFeaturesFunction,
  } = options

  const store = useDetailStore()

  const { ehrId, ehrGeometry, cadasterId } = storeToRefs(store)

  const mapRef = useTemplateRef('mapRef')
  const loading = ref(true)
  const status = ref(true)
  const center = shallowRef(transform('fromLonLat', MAP_CENTER, MAP_PROJECTION))
  const zoom = ref(MAP_ZOOM)
  const overlay = shallowRef(null)
  const errorMessage = ref(null)

  const infoLayers = computed(() =>
    infoLayersIds.map(layer => findLayer(layer)),
  )

  const mapview = computed(() => ({
    extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
    minZoom: MAP_MINZOOM,
    maxZoom: MAP_MAXZOOM,
    projection: MAP_PROJECTION,
    baseLayers: [findLayer('ma-map')],
  }))

  const featureLayer = () => {
    const layer = findLayer(featureLayerId)
    return layer
  }

  const createMapFeatures = (id, geometry) => {
    if (!geometry || !geometry.length) return
    // No EHR building id = land parcel (its geometry is the cadastre shape).
    const isLandParcel = !ehrId.value
    createMapFeaturesFunction(id, geometry, featureLayer, overlay, center, zoom, isLandParcel)
  }

  const exportMap = async () => {
    if (status.value && mapRef.value) {
      const map = await mapRef.value.exportMap()
      store.setMapExport({ key: mapType, value: map })
    } else {
      store.setMapExport({ key: mapType, value: '' })
    }
  }

  onMounted(() => {
    if (!ehrGeometry.value) {
      errorMessage.value = t('messages.detail.errors.ehr_geometry_not_found')
      loading.value = false
      status.value = false
      return
    }

    loading.value = true

    try {
      // Land parcels have no EHR building — ehrGeometry falls back to the
      // cadastre shape (store computed) and the feature id to the tunnus.
      const featureId = ehrId.value ?? cadasterId.value
      if (ehrGeometry.value && featureId) {
        createMapFeatures(featureId, ehrGeometry.value)
      }
    } catch (error) {
      console.error(
        'useMicroMacroDetailMap: Error creating map features:',
        error,
      )
    } finally {
      loading.value = false
      errorMessage.value = null
    }
  })

  return {
    mapRef,
    loading,
    status,
    center,
    zoom,
    overlay,
    infoLayers,
    mapview,
    exportMap,
    errorMessage,
  }
}
